import { memo, useCallback, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import { ALL_RESIZE_ANCHORS, ResizeAnchor } from '@/enums/dragKind';
import type { NoteColor } from '@/enums/noteColor';
import { useDrag } from '@/hooks/useDrag';
import { useNotesDispatch } from '@/hooks/useNotesDispatch';
import { useInteraction } from '@/hooks/useInteraction';
import { bringToFront, deleteNote, move, resize, updateColor, updateText } from '@/state/notesActions';
import type { Note as NoteType, NoteId } from '@/types/note';
import type { Rect } from '@/types/geometry';
import { pointInRect } from '@/utils/geometry';
import { computeResizedRect } from '@/utils/resize';
import { clamp } from '@/utils/clamp';
import {
  DOUBLE_CLICK_DIST_PX,
  DOUBLE_CLICK_MS,
  KEYBOARD_NUDGE_LARGE_PX,
  KEYBOARD_NUDGE_PX,
  MAX_NOTE_SIZE,
  MIN_NOTE_SIZE,
} from '@/constants/notes';
import { NoteEditor } from './NoteEditor';
import { NoteHeader } from './NoteHeader';
import { ResizeHandle } from './ResizeHandle';
import styles from './Note.module.css';

interface Props {
  readonly note: NoteType;
}

interface MoveCache {
  readonly initial: Rect;
  readonly trashRect: DOMRect | null;
}

interface ResizeCache {
  readonly initial: Rect;
  readonly anchor: ResizeAnchor;
}

/**
 * A single sticky note.
 *
 * Interaction responsibilities:
 *   - Drag from anywhere on the note body (except header/handles/editor) → Move
 *   - 8 resize handles → Resize (anchor-aware)
 *   - Trash detection during Move: if pointerup happens inside the trash rect,
 *     dispatch Delete instead of Move.
 *   - Direct-DOM style writes during gesture; single dispatch on release.
 *
 * Perf: memoized on the note prop (referential equality via reducer immutability).
 * Handlers rely on the note ref, not on state, to keep them stable per note.
 */
export const Note = memo(function Note({ note }: Props) {
  const dispatch = useNotesDispatch();
  const { trashRef, boardRef } = useInteraction();
  const noteRef = useRef<HTMLDivElement | null>(null);
  const [editing, setEditing] = useState(false);
  // Manual double-click detection: useDrag preventDefaults pointerdown, which
  // suppresses the browser's compat click/dblclick events. Track the last
  // "click" (drag with tiny distance) and open the editor on the second one.
  const lastClickAtRef = useRef<number>(0);

  // --- Move gesture ---------------------------------------------------------
  const beginMove = useCallback(
    (_event: ReactPointerEvent<HTMLElement>): MoveCache => {
      dispatch(bringToFront(note.id));
      const trashRect = trashRef.current?.getBoundingClientRect() ?? null;
      return {
        initial: { x: note.x, y: note.y, width: note.width, height: note.height },
        trashRect,
      };
    },
    [dispatch, note.id, note.x, note.y, note.width, note.height, trashRef],
  );

  const applyMoveTransform = useCallback(
    (dx: number, dy: number, cache: MoveCache, pointerEvent: PointerEvent) => {
      const el = noteRef.current;
      if (!el) return;
      const nx = cache.initial.x + dx;
      const ny = cache.initial.y + dy;
      el.style.transform = `translate3d(${nx}px, ${ny}px, 0)`;

      const overTrash =
        cache.trashRect !== null &&
        pointInRect(
          { x: pointerEvent.clientX, y: pointerEvent.clientY },
          {
            x: cache.trashRect.x,
            y: cache.trashRect.y,
            width: cache.trashRect.width,
            height: cache.trashRect.height,
          },
        );

      el.classList.toggle(styles.overTrash, overTrash);
      trashRef.current?.classList.toggle('trash-zone--active', overTrash);
    },
    [trashRef],
  );

  const endMove = useCallback(
    (id: NoteId, cache: MoveCache, dx: number, dy: number, pointerEvent: PointerEvent, distance: number) => {
      const el = noteRef.current;
      if (el) {
        el.classList.remove(styles.overTrash);
        // Keep the transform as-is so React's next render (which will set the
        // same transform via style prop) doesn't cause a visual jump.
      }
      trashRef.current?.classList.remove('trash-zone--active');

      // Click (barely moved): detect double-click for opening the editor.
      if (distance < DOUBLE_CLICK_DIST_PX) {
        const now = performance.now();
        if (now - lastClickAtRef.current < DOUBLE_CLICK_MS) {
          setEditing(true);
          lastClickAtRef.current = 0;
        } else {
          lastClickAtRef.current = now;
        }
        return;
      }
      lastClickAtRef.current = 0;

      const overTrash =
        cache.trashRect !== null &&
        pointInRect(
          { x: pointerEvent.clientX, y: pointerEvent.clientY },
          {
            x: cache.trashRect.x,
            y: cache.trashRect.y,
            width: cache.trashRect.width,
            height: cache.trashRect.height,
          },
        );

      if (overTrash) {
        dispatch(deleteNote(id));
        return;
      }

      const board = boardRef.current;
      const boardRect = board?.getBoundingClientRect();
      const boardBounds = boardRect
        ? { minX: 0, minY: 0, maxX: boardRect.width, maxY: boardRect.height }
        : null;

      let nx = cache.initial.x + dx;
      let ny = cache.initial.y + dy;
      if (boardBounds) {
        nx = clamp(nx, boardBounds.minX, Math.max(boardBounds.minX, boardBounds.maxX - cache.initial.width));
        ny = clamp(ny, boardBounds.minY, Math.max(boardBounds.minY, boardBounds.maxY - cache.initial.height));
      }
      dispatch(move({ id, x: nx, y: ny }));
    },
    [dispatch, boardRef, trashRef],
  );

  const cancelMove = useCallback(() => {
    const el = noteRef.current;
    if (el) {
      el.classList.remove(styles.overTrash);
      el.style.transform = `translate3d(${note.x}px, ${note.y}px, 0)`;
    }
    trashRef.current?.classList.remove(styles.trashActive);
  }, [note.x, note.y, trashRef]);

  const onMovePointerDown = useDrag<MoveCache>({
    onStart: beginMove,
    onDrag: ({ delta, cache, pointerEvent }) => applyMoveTransform(delta.dx, delta.dy, cache, pointerEvent),
    onEnd: ({ delta, cache, pointerEvent, distance }) =>
      endMove(note.id, cache, delta.dx, delta.dy, pointerEvent, distance),
    onCancel: cancelMove,
  });

  // --- Resize gesture (per handle) ------------------------------------------
  const applyResizeTransform = useCallback((rect: Rect) => {
    const el = noteRef.current;
    if (!el) return;
    el.style.transform = `translate3d(${rect.x}px, ${rect.y}px, 0)`;
    el.style.width = `${rect.width}px`;
    el.style.height = `${rect.height}px`;
  }, []);

  const onResizePointerDown = useDrag<ResizeCache>({
    onStart: (event) => {
      dispatch(bringToFront(note.id));
      const anchor = (event.currentTarget.dataset['anchor'] ?? ResizeAnchor.BottomRight) as ResizeAnchor;
      return {
        anchor,
        initial: { x: note.x, y: note.y, width: note.width, height: note.height },
      };
    },
    onDrag: ({ delta, cache }) => {
      const next = computeResizedRect(cache.anchor, cache.initial, delta.dx, delta.dy, MIN_NOTE_SIZE, MAX_NOTE_SIZE);
      applyResizeTransform(next);
    },
    onEnd: ({ delta, cache }) => {
      const next = computeResizedRect(cache.anchor, cache.initial, delta.dx, delta.dy, MIN_NOTE_SIZE, MAX_NOTE_SIZE);
      dispatch(
        resize({
          id: note.id,
          width: next.width,
          height: next.height,
          x: next.x,
          y: next.y,
        }),
      );
    },
    onCancel: () => {
      applyResizeTransform({ x: note.x, y: note.y, width: note.width, height: note.height });
    },
  });

  // --- Header actions -------------------------------------------------------
  const handleColorChange = useCallback(
    (color: NoteColor) => dispatch(updateColor(note.id, color)),
    [dispatch, note.id],
  );
  const handleDelete = useCallback(() => dispatch(deleteNote(note.id)), [dispatch, note.id]);

  // --- Editor ---------------------------------------------------------------
  const commitEdit = useCallback(
    (text: string) => {
      dispatch(updateText(note.id, text));
      setEditing(false);
    },
    [dispatch, note.id],
  );
  const cancelEdit = useCallback(() => setEditing(false), []);

  // --- Keyboard shortcuts for a focused note --------------------------------
  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (editing) return;
      const step = event.shiftKey ? KEYBOARD_NUDGE_LARGE_PX : KEYBOARD_NUDGE_PX;
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          dispatch(move({ id: note.id, x: note.x, y: note.y - step }));
          break;
        case 'ArrowDown':
          event.preventDefault();
          dispatch(move({ id: note.id, x: note.x, y: note.y + step }));
          break;
        case 'ArrowLeft':
          event.preventDefault();
          dispatch(move({ id: note.id, x: note.x - step, y: note.y }));
          break;
        case 'ArrowRight':
          event.preventDefault();
          dispatch(move({ id: note.id, x: note.x + step, y: note.y }));
          break;
        case 'Enter':
          event.preventDefault();
          setEditing(true);
          break;
        case 'Delete':
        case 'Backspace':
          event.preventDefault();
          dispatch(deleteNote(note.id));
          break;
      }
    },
    [dispatch, editing, note.id, note.x, note.y],
  );

  const bringToFrontOnFocus = useCallback(() => {
    dispatch(bringToFront(note.id));
  }, [dispatch, note.id]);

  return (
    <div
      ref={noteRef}
      className={`${styles.note} ${styles[`color_${note.color}`]}`}
      style={{
        transform: `translate3d(${note.x}px, ${note.y}px, 0)`,
        width: note.width,
        height: note.height,
        zIndex: note.zIndex,
      }}
      role="group"
      aria-label={`Note ${note.text ? `— ${note.text.slice(0, 40)}` : ''}`}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onFocus={bringToFrontOnFocus}
      onPointerDown={onMovePointerDown}
      data-note-id={note.id}
    >
      <NoteHeader color={note.color} onColorChange={handleColorChange} onDelete={handleDelete} />
      <NoteEditor
        text={note.text}
        editing={editing}
        onCommit={commitEdit}
        onCancel={cancelEdit}
      />
      {ALL_RESIZE_ANCHORS.map((anchor) => (
        <ResizeHandle key={anchor} anchor={anchor} onPointerDown={onResizePointerDown} />
      ))}
    </div>
  );
});
